// PackageModal: Create/Edit package with global categories, includes, hotels
import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, Box, Chip, Typography, Autocomplete, CircularProgress, InputAdornment } from '@mui/material';
import api from '../services/api';

const CATEGORY_OPTIONS = [
  { value: 'delegate', label: 'Delegate', icon: '👤' },
  { value: 'exhibitor', label: 'Exhibitor', icon: '🏢' },
  { value: 'sponsorship', label: 'Sponsor', icon: '💼' },
  { value: 'speaker', label: 'Speaker', icon: '🎤' }
];



function PackageModal({ open, onClose, onSave, editPackage }) {
  const [form, setForm] = useState({
    name: '',
    category: '',
    price: '',
    includes: [],
    description: '',
    capacity: '',
    eventId: '',
    hotels: [],
  });
  // Removed hotels state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1);
  const [events, setEvents] = useState([]);
  const [eventsError, setEventsError] = useState("");
  // Fetch events for dropdown
  useEffect(() => {
    api.get('/api/events')
      .then(res => {
        setEvents(res.data.events || res.data || []);
        setEventsError("");
      })
      .catch(err => {
        setEvents([]);
        if (err.response && err.response.status === 401) {
          setEventsError("You must be logged in as admin/staff to view events. Please log in.");
        } else {
          setEventsError("Failed to load events. Please try again or contact support.");
        }
      });
  }, []);

  useEffect(() => {
    if (editPackage) {
      setForm({ ...editPackage, hotels: editPackage.hotels || [] });
      setStep(2);
    } else {
      setForm({ name: '', category: '', price: '', includes: [], description: '', capacity: '', eventId: '', hotels: [] });
      setStep(1);
    }
  }, [editPackage, open]);

  // Removed hotels fetch

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };
  const handleIncludesChange = (e, value) => setForm(f => ({ ...f, includes: value }));
  // Removed handleHotelsChange

  const validate = () => {
    if (!form.name.trim()) return 'Name is required';
    if (!form.category) return 'Category is required';
    if (!form.price || isNaN(Number(form.price)) || Number(form.price) < 0) return 'Valid price required';
    if (!form.capacity || isNaN(Number(form.capacity)) || Number(form.capacity) < 1) return 'Valid capacity required';
    return '';
  };

  // Custom includes input: comma-separated, chips view
  const [includesInput, setIncludesInput] = useState('');
  const handleIncludesInputChange = (e) => setIncludesInput(e.target.value);
  const handleIncludesInputKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && includesInput.trim()) {
      e.preventDefault();
      const newItem = includesInput.trim().replace(/,$/, '');
      if (newItem && !form.includes.includes(newItem)) {
        setForm(f => ({ ...f, includes: [...f.includes, newItem] }));
      }
      setIncludesInput('');
    }
  };
  const handleRemoveInclude = (idx) => {
    setForm(f => ({ ...f, includes: f.includes.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = async () => {
    const v = validate();
    if (v) { setError(v); return; }
    setLoading(true);
    setError('');
    try {
      const payload = { ...form, price: Number(form.price), capacity: Number(form.capacity), hotels: form.hotels.map(h => h._id) };
      if (editPackage) await api.put(`/api/packages/${editPackage._id}`, payload);
      else await api.post('/api/packages', payload);
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editPackage ? 'Edit Package' : step === 1 ? 'Choose Category' : 'Create Package'}</DialogTitle>
      <DialogContent>
        {step === 1 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap', py: 2 }}>
            {CATEGORY_OPTIONS.map(opt => (
              <Button
                key={opt.value}
                variant={form.category === opt.value ? 'contained' : 'outlined'}
                onClick={() => setForm(f => ({ ...f, category: opt.value }))}
                sx={{
                  minWidth: 120,
                  minHeight: 110,
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 600, fontSize: 18, borderRadius: 2, boxShadow: form.category === opt.value ? 3 : 1,
                  bgcolor: form.category === opt.value ? 'primary.main' : 'background.paper',
                  color: form.category === opt.value ? 'primary.contrastText' : 'text.primary',
                  border: form.category === opt.value ? '2px solid #007bff' : '1.5px solid #ccc',
                  transition: 'all 0.2s',
                  mb: 2
                }}
              >
                <span style={{ fontSize: 40, marginBottom: 8 }}>{opt.icon}</span>
                {opt.label}
              </Button>
            ))}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              select
              label="Event"
              name="eventId"
              value={form.eventId}
              onChange={handleChange}
              fullWidth
              required
            >
              <MenuItem value="">Select Event</MenuItem>
              {eventsError ? (
                <MenuItem disabled>{eventsError}</MenuItem>
              ) : events.length === 0 ? (
                <MenuItem disabled>No events found.</MenuItem>
              ) : events.map(ev => (
                <MenuItem key={ev._id} value={ev._id}>
                  <Box>
                    <b>{ev.title || ev.name}</b>
                    {ev.venue && <span style={{ marginLeft: 8, color: '#888' }}>@ {ev.venue}</span>}
                    {ev.startDate && (
                      <span style={{ marginLeft: 8, color: '#888' }}>
                        {new Date(ev.startDate).toLocaleDateString()} {ev.endDate ? ' - ' + new Date(ev.endDate).toLocaleDateString() : ''}
                      </span>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
            <TextField label="Name" name="name" value={form.name} onChange={handleChange} fullWidth required />
            <TextField select label="Category" name="category" value={form.category} onChange={handleChange} fullWidth required disabled>
              {CATEGORY_OPTIONS.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
            </TextField>
            <TextField label="Price" name="price" value={form.price} onChange={handleChange} type="number" fullWidth required InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
            <TextField label="Capacity" name="capacity" value={form.capacity} onChange={handleChange} type="number" fullWidth required />
            <TextField label="Description" name="description" value={form.description} onChange={handleChange} fullWidth multiline rows={2} />
            {/* Includes input: chips for each comma-separated value */}
            <Box>
              <TextField
                label="Includes (comma separated)"
                value={includesInput}
                onChange={handleIncludesInputChange}
                onKeyDown={handleIncludesInputKeyDown}
                fullWidth
                placeholder="Type and press Enter or comma"
              />
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {form.includes.map((inc, idx) => (
                  <Chip key={idx} label={inc} onDelete={() => handleRemoveInclude(idx)} />
                ))}
              </Box>
            </Box>
            {error && <Typography color="error" mb={1}>{error}</Typography>}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {step === 1 ? (
          <Button onClick={onClose}>Cancel</Button>
        ) : (
          <>
            <Button onClick={() => setStep(1)}>Back</Button>
            <Button onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained" disabled={loading}>{loading ? <CircularProgress size={22} /> : 'Save'}</Button>
          </>
        )}
        {step === 1 && (
          <Button
            onClick={() => form.category ? setStep(2) : null}
            variant="contained"
            disabled={!form.category}
          >Next</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default PackageModal;
